import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import FilterIcon from '@material-ui/icons/FilterList';

// import OverviewIcon from '../../../assets/icons/OverviewIcon';
import DocumentsIcon from '../../../assets/icons/DocumentIcon';
import LeadActivityIcon from '../../../assets/icons/LeadActivityIcon';
import NotesIcon from '../../../assets/icons/NotesIcon';
import AddIcon from '../../../assets/icons/AddIcon';

import {
  LeadActivity,
  LeadDocuments,
  LeadInfo,
  LeadNoteModal,
  LeadNotes,
  LostLeadModal,
  WonLeadModal,
} from '../../../components/leads';
import { LeadModal } from '../../../components/sales';
import ConfirmationModal from '../../../components/shared/modal/ConfirmationModal';
import ActionIconButton from '../../../components/shared/ActionIconButton';
import DocumentsUploadModal from '../../../components/leads/DocumentsUploadModal';
import LoadScreen from '../../../components/load-screen';
import Tabs from '../../../components/shared/Tabs';
import ActiveSurgeonLeadModal from '../../../components/leads/ActiveSurgeonLeadModal';

import { getSurgeons, getQualifications, getSpecialities } from '../../../actions/surgeonsActions';
import { getProcedures } from '../../../actions/proceduresActions';
import {
  clearLead, getLead, subscribeToLeadActivity, subscribeToLeadDocuments,
  uploadDocuments, deleteDocument, updateLead, getStageLeads, archiveLead, onUploadComplete,
  subscribeToLeadNotes, createLeadNote, makeLeadLost, reactivateLead, makeLeadWon, createLeadActivity,
  updateLeadNote, updateLeadActivity,
} from '../../../actions/leadsActions';
import { getPipelineLabels, getPipelines, getPipelineStages } from '../../../actions/salesActions';
import { getCompletedCases } from '../../../actions/casesActions';

import { getCasesList } from '../../../reducers/selectors';

import { routes } from '../../../constants';
import { leadStatuses } from '../../../constants/sales';

import './lead-page.scss';

// const viewOptions = {
//   // overview: { label: 'Overview', value: 'OVERVIEW', icon: <OverviewIcon /> },
//   activity: { label: 'Activity', value: 'ACTIVITY', icon: <LeadActivityIcon /> },
//   documents: { label: 'Documents', value: 'DOCUMENTS', icon: <DocumentsIcon /> },
// };

const tabs = [
  { label: 'Notes', icon: <NotesIcon color="var(--app-text)" /> },
  { label: 'Activity', icon: <LeadActivityIcon color="var(--app-text)" /> },
  { label: 'Documents', icon: <DocumentsIcon color="var(--app-text)" /> },
];

const LeadPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const { search } = location;

  const queryParams = new URLSearchParams(search);

  const from = queryParams.get('from');
  // const defaultView = queryParams.get('view');

  const leadId = id.replace(':', '');

  const dispatch = useDispatch();

  const activeLead = useSelector((state) => state.activeLead.data);
  const leadDocuments = useSelector((state) => state.activeLead.documents);
  const leadActivity = useSelector((state) => state.activeLead.activity);
  const leadNotes = useSelector((state) => state.activeLead.notes);
  // const view = useSelector((state) => state.activeLead.view);
  const cases = useSelector(getCasesList);
  const completedLoaded = useSelector((state) => state.cases.completedLoaded);

  const currentUser = useSelector((state) => state.user.currentUser);
  const surgeons = useSelector((state) => state.surgeons.list);
  const qualifications = useSelector((state) => state?.surgeons.qualifications);
  const specialities = useSelector((state) => state.surgeons.specialities);
  const procedures = useSelector((state) => state.procedures.list);
  const users = useSelector((state) => state.users.list);
  const pipelines = useSelector((state) => state.pipelines?.list || []);
  const labels = useSelector((state) => state.pipelines?.labels || []);
  const currentTenant = useSelector((state) => state.tenant.currentTenant);

  const [activeTab, setActiveTab] = useState(0);

  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [lostLoading, setLostLoading] = useState(false);
  const [wonLoading, setWonLoading] = useState(false);

  const [leadModalOpen, toggleLeadModal] = useState(false);
  const [activeSurgeonModalOpen, toggleActiveSurgeonModal] = useState(false);
  const [archiveModalOpen, toggleArchiveModal] = useState(false);
  const [documentsUploadModalOpen, toggleDocumentsUploadModal] = useState(false);
  const [noteModalOpen, toggleNoteModal] = useState(false);
  const [lostLeadModalOpen, toggleLostLeadModal] = useState(false);
  const [wonLeadModalOpen, toggleWonLeadModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);

  const [allStages, setAllStages] = useState([]);
  const [reactivateMode, toggleReactivateMode] = useState(false);

  const [addActivityMode, toggleAddActivityMode] = useState(false);
  const [addActivityLoading, toggleAddActivityLoading] = useState(false);

  // const updateParam = (key, value) => {
  //   const searchParams = new URLSearchParams(location.search);
  //   searchParams.set(key, value);
  //   history.push({ search: searchParams.toString() });
  // };

  // const setView = (view) => {
  //   dispatch(setLeadView(view));
  //   updateParam('view', view);
  // };

  // useEffect(() => {
  //   setView(defaultView || viewOptions.activity.value);
  // }, [defaultView]);

  useEffect(() => {
    onLoad().catch();

    const unsubscribeToLeadActivity = dispatch(subscribeToLeadActivity(leadId));
    const unsubscribeToLeadDocuments = dispatch(subscribeToLeadDocuments(leadId));
    const unsubscribeToLeadNotes = dispatch(subscribeToLeadNotes(leadId));

    return () => {
      dispatch(clearLead());
      unsubscribeToLeadActivity();
      unsubscribeToLeadDocuments();
      unsubscribeToLeadNotes();
    };
  }, []);

  const onLoad = async () => {
    try {
      setFetching(true);
      await dispatch(getLead(leadId));

      const res = await dispatch(getPipelines());
      if (res && res.length) {
        const stages = await fetchAllStages(res?.map((p) => p.id));
        setAllStages(stages);
      }

      if (!surgeons?.length || !procedures?.length || !labels.length || !qualifications.length) {
        await Promise.all([
          dispatch(getSurgeons()),
          dispatch(getProcedures()),
          dispatch(getPipelineLabels()),
          dispatch(getQualifications()),
          dispatch(getSpecialities())
        ]);
      }

      if (!completedLoaded) {
        await dispatch(getCompletedCases());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const fetchAllStages = async (pipelineIds) => {
    try {
      let arr = [];
      for (const pipelineId of pipelineIds) {
        const res = await dispatch(getPipelineStages(pipelineId));
        if (res) {
          arr = [...arr, ...res];
        }
      }
      setAllStages(arr);
      return arr;
    } catch (err) {
      console.error(err);
    }
  };

  const onBackClick = () => {
    if (from === 'SURGEON' && activeLead.surgeon) {
      history.push(`${routes.TOOLS_SURGEONS}/${activeLead.surgeon}`);
    } else {
      if (activeLead.status === leadStatuses.LOST.value) {
        history.push(routes.SALES_LOST_LEADS);
      } else if (activeLead.status === leadStatuses.WON.value) {
        history.push(routes.SALES_ACTIVE_SURGEONS);
      } else {
        history.push(routes.SALES_LEADS);
      }
    }
  };

  const onSurgeonClick = (surgeonId) => {
    history.push(`${routes.TOOLS_SURGEONS}/${surgeonId}?leadId=${activeLead.id}`);
  };

  const onUploadDocuments = (files) => {
    dispatch(uploadDocuments(id, files));
  };

  const onDeleteDocument = (fileId) => {
    dispatch(deleteDocument(id, fileId));
  };

  const onUpdateLead = async (formObj) => {
    try {
      if (activeLead) {
        setLoading(true);

        const data = { ...formObj };
        if (formObj.stage !== activeLead.stage) {
          const stageLeads = await dispatch(getStageLeads(formObj.stage));
          data.orderNumber = stageLeads.length + 1;
        }
        await dispatch(updateLead(activeLead.id, data));
        await dispatch(getLead(activeLead.id));

        toggleLeadModal(false);
        toggleActiveSurgeonModal(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onReactivateLead = async (formObj) => {
    try {
      if (activeLead) {
        setLoading(true);

        const data = { ...formObj };
        const stageLeads = await dispatch(getStageLeads(formObj.stage));
        data.orderNumber = stageLeads.length + 1;
        await dispatch(reactivateLead(activeLead.id, data));
        await dispatch(getLead(activeLead.id));

        toggleLeadModal(false);
        toggleReactivateMode(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onLeadSubmit = async (formObj) => {
    if (reactivateMode) {
      await onReactivateLead(formObj);
    } else {
      await onUpdateLead(formObj);
    }
  };
  
  const onArchiveLead = async () => {
    try {
      setArchiveLoading(true);
      await dispatch(archiveLead(activeLead.id));
      toggleArchiveModal(false);
      history.push(routes.SALES_LEADS);
    } catch (err) {
      console.error(err);
    } finally {
      setArchiveLoading(false);
    }
  };

  const onUpload = (fileName, downloadUrl) => {
    dispatch(onUploadComplete(leadId, { name: fileName, downloadUrl }));
  };

  const onNoteSubmit = async (note) => {
    try {
      setNoteLoading(true);
      if (selectedNote?.id && selectedNote.authorId === currentUser.uid) {
        await dispatch(updateLeadNote(activeLead.id, selectedNote.id, { note }));
      } else {
        await dispatch(createLeadNote(activeLead.id, { note }));
      }
      toggleNoteModal(false);
      setSelectedNote(null);
    } catch (err) {
      console.error(err);
    } finally {
      setNoteLoading(false);
    }
  };

  const onLostLeadSubmit = async (formObj) => {
    try {
      setLostLoading(true);
      const data = {
        lostDate: formObj.date,
        lostNotes: formObj.notes,
        stage: formObj.stage,
        owner: formObj.owner,
      };
      await dispatch(makeLeadLost(activeLead.id, data));
      await dispatch(getLead(activeLead.id));
      toggleLostLeadModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLostLoading(false);
    }
  };

  const onWonLeadSubmit = async (formObj) => {
    try {
      setWonLoading(true);
      await dispatch(makeLeadWon(activeLead.id, { ...formObj, surgeon: activeLead.surgeon }));
      await dispatch(getLead(activeLead.id));
      toggleWonLeadModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setWonLoading(false);
    }
  };

  const onAddActivity = async (formObj) => {
    if (activeLead.id) {
      try {
        toggleAddActivityLoading(true);
        if (selectedActivity?.id && selectedActivity.userId === currentUser.uid) {
          await dispatch(updateLeadActivity(activeLead.id, selectedActivity.id, formObj));
        } else {
          await dispatch(createLeadActivity(activeLead.id, formObj));
        }
        toggleAddActivityMode(false);
        setSelectedActivity(null);
      } catch (err) {
        console.error(err);
      } finally {
        toggleAddActivityLoading(false);
      }
    }
  };

  const onEditNote = (note) => {
    if (note?.authorId !== currentUser.uid) {
      return;
    }

    setSelectedNote(note);
    toggleNoteModal(true);
  };

  const onEditActivity = (activityItem) => {
    if (activityItem?.userId !== currentUser.uid) {
      return;
    }

    setSelectedActivity(activityItem);
    toggleAddActivityMode(true);
  };

  const renderView = () => {
    switch (activeTab) {
      case 0:
        return (
          <LeadNotes
            notes={leadNotes}
            users={users}
            currentUserId={currentUser.uid}
            onEditClick={onEditNote}
          />
        );
      case 1:
        return (
          <LeadActivity
            activity={leadActivity}
            users={users}
            addActivityMode={addActivityMode}
            onCancel={() => {
              toggleAddActivityMode(false);
              setSelectedActivity(null);
            }}
            onAddActivity={onAddActivity}
            loading={addActivityLoading}
            currentUserId={currentUser.uid}
            selectedActivity={selectedActivity}
            onEditClick={onEditActivity}
          />
        );
      case 2:
        return (
          <LeadDocuments
            documents={leadDocuments}
            onUpload={onUploadDocuments}
            onDelete={onDeleteDocument}
            leadId={leadId}
            tenantId={currentTenant.id}
            tenantColor={currentTenant.colorPrimary}
            users={users}
          />
        );
      default:
        return null;
    }
  };

  const renderActions = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className='d-flex'>
            <div>
              <ActionIconButton className='lead-page__toolbar-button lead-page__toolbar-button--filter'>
                <FilterIcon />
              </ActionIconButton>
            </div>
            <div className='m-l-md'>
              <ActionIconButton
                className='lead-page__toolbar-button lead-page__toolbar-button--add'
                color='blue'
                onClick={() => {
                  setSelectedNote(null);
                  toggleNoteModal(true);
                }}
              >
                <AddIcon />
              </ActionIconButton>
            </div>
          </div>
        );
      case 1:
        return <div className='d-flex'>
          <div>
            <ActionIconButton className='lead-page__toolbar-button lead-page__toolbar-button--filter'>
              <FilterIcon />
            </ActionIconButton>
          </div>
          <div className='m-l-md'>
            <ActionIconButton
              className='lead-page__toolbar-button lead-page__toolbar-button--add'
              color='blue'
              onClick={() => {
                setSelectedActivity(null);
                toggleAddActivityMode(true);
              }}
            >
              <AddIcon />
            </ActionIconButton>
          </div>
        </div>;
      case 2:
        return (
          <div className='d-flex'>
            <div>
              <ActionIconButton className='lead-page__toolbar-button lead-page__toolbar-button--filter'>
                <FilterIcon />
              </ActionIconButton>
            </div>
            <div className='m-l-md'>
              <ActionIconButton
                className='lead-page__toolbar-button lead-page__toolbar-button--add'
                color='blue'
                onClick={() => toggleDocumentsUploadModal(true)}
              >
                <AddIcon />
              </ActionIconButton>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {fetching && <LoadScreen />}
      <div className='lead-page__container'>
        <div>
          <LeadInfo
            lead={activeLead}
            activity={leadActivity}
            cases={cases}
            labels={labels}
            users={users}
            surgeons={surgeons}
            specialities={specialities}
            pipelines={pipelines}
            stages={allStages}
            qualifications={qualifications}
            onBackClick={onBackClick}
            onSurgeonClick={onSurgeonClick}
            onEditClick={() => {
              if (activeLead?.status === leadStatuses.WON.value) {
                toggleActiveSurgeonModal(true);
              } else {
                toggleLeadModal(true)
              }
            }}
            onDeleteClick={() => toggleArchiveModal(true)}
            onLostClick={() => toggleLostLeadModal(true)}
            onWonClick={() => toggleWonLeadModal(true)}
            onReactivateClick={() => {
              toggleReactivateMode(true);
              toggleLeadModal(true);
            }}
            isArchived={!activeLead.active}
          />
        </div>
        <div>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            fontSize={14}
            renderActions={renderActions}
          >
            {renderView()}
          </Tabs>
        </div>

        {activeLead?.status === leadStatuses.WON.value ? (
          <ActiveSurgeonLeadModal
            open={activeSurgeonModalOpen}
            onClose={() => toggleActiveSurgeonModal(false)}
            surgeonsList={surgeons}
            proceduresList={procedures}
            specialities={specialities}
            labelsList={labels}
            usersList={users}
            pipelines={pipelines}
            stages={allStages}
            defaultPipeline={activeLead.pipeline}
            mode='update'
            lead={activeLead}
            loading={loading}
            onSubmit={onLeadSubmit}
            currentUser={currentUser}
            tenantColor={currentTenant?.colorPrimary}
            surgeonRanking={currentTenant?.surgeonRanking}
          />
        ) : (
          <LeadModal
            open={leadModalOpen}
            onClose={() => {
              toggleLeadModal(false);
              toggleReactivateMode(false);
            }}
            surgeonsList={surgeons}
            proceduresList={procedures}
            labelsList={labels}
            usersList={users}
            pipelines={pipelines}
            stages={allStages}
            defaultPipeline={activeLead.pipeline}
            mode={reactivateMode ? 'reactivate' : 'update'}
            lead={activeLead}
            loading={loading}
            onSubmit={onLeadSubmit}
            currentUser={currentUser}
            tenantColor={currentTenant?.colorPrimary}
            surgeonRanking={currentTenant?.surgeonRanking}
          />
        )}

        <ConfirmationModal
          open={archiveModalOpen}
          onClose={() => toggleArchiveModal(false)}
          onSubmit={onArchiveLead}
          title="Are you sure you want to archive this lead?"
          submitText="Archive"
          loading={archiveLoading}
        />

        <DocumentsUploadModal
          open={documentsUploadModalOpen}
          onClose={() => toggleDocumentsUploadModal(false)}
          leadId={leadId}
          tenantId={currentTenant.id}
          tenantColor={currentTenant.colorPrimary}
          onUploadComplete={onUpload}
        />

        <LeadNoteModal
          open={noteModalOpen}
          onClose={() => {
            toggleNoteModal(false);
            setSelectedNote(null);
          }}
          onSubmit={onNoteSubmit}
          loading={noteLoading}
          initialValue={selectedNote?.note || ''}
          mode={selectedNote ? 'edit' : 'create'}
        />

        {!!activeLead && (
          <LostLeadModal
            open={lostLeadModalOpen}
            onClose={() => toggleLostLeadModal(false)}
            loading={lostLoading}
            onSubmit={onLostLeadSubmit}
            lead={activeLead}
            usersList={users}
            stages={allStages}
          />
        )}

        {!!activeLead && (
          <WonLeadModal
            open={wonLeadModalOpen}
            onClose={() => toggleWonLeadModal(false)}
            loading={wonLoading}
            onSubmit={onWonLeadSubmit}
            lead={activeLead}
            usersList={users}
            stages={allStages}
            specialities={specialities}
            surgeons={surgeons}
            proceduresList={procedures}
            labelsList={labels}
            tenantColor={currentTenant.colorPrimary}
            surgeonRanking={currentTenant.surgeonRanking}
          />
        )}
      </div>
    </>
  )
};

export default LeadPage;
